/**
 * Async functions for communicating with Control Tower, the HTTP service powering ML-related
 * features like adaptive audiences. See https://github.com/optimizely/control-tower/tree/master/control-tower.
 *
 * @module ControlTower
 */

var _ = require('optimizely-client-fn');
var createError = require('core/utils/custom_error').create;
var JSONEnv = require('core/lib/json');
var XHR = require('core/utils/xhr');

var AdaGaugeError = createError('AdaGaugeError');
if (__TEST__) {
  exports.AdaGaugeError = AdaGaugeError;
}

// URL from which to obtain InterestGroup scores.
var GAUGE_URL = __CONTROL_TOWER_ORIGIN__ + '/v1/interests/gauge';

// These are static in the current implementation of adaptive audiences.
var PARTS_OF_SPEECH = ['NN', 'VB', 'JJ'];

/**
 * @typedef {Object} AdaEvent
 * @property {string} eventName
 *        Page ID
 * @property {number} timestamp
 *        Milliseconds since Unix epoch at which the event occurred.
 * @property {Object.<Object>} content
 *        An object like {text: {tag: string, value: string}}.
 */

/**
 * @typedef {Object} GaugeData
 * @property {Object.<InterestGroup>} interestGroups
 *        Map of InterestGroup ID -> InterestGroup. There will be one score in the
 *        gauge response for each of the InterestGroups here.
 * @property {Object.<TagGroup>} tagGroups
 *        Map of TagGroup ID -> TagGroup.
 *        TagGroups specify which tags to include (and, in effect, exclude) from consideration
 *        when computing a given InterestGroup's score.
 * @property {Array.<module:ControlTower~AdaEvent>} events
 *        The data that's the basis for InterestGroup scores.
 */

/**
 * @param {module:ControlTower~GaugeData} data
 * @returns {PromiseEnv.<Object, module:ControlTower~AdaGaugeError>}
 *        Rejects when the service can't be reached or gives a response that doesn't
 *          resemble a JSON-serialized object.
 *        Fulfills otherwise with the object that the gauge API returned.
 */
exports.gauge = function gauge(data:import('ControlTower').GaugeData): Promise<any> {
  var body = formatRequestBody(data);

  return XHR.request({
    method: 'POST',
    url: GAUGE_URL,
    contentType: 'application/json',
    data: body,
  })
  .then(function networkSuccess(xhr) {
    if (xhr.status > 299) {
      throw new AdaGaugeError('Status: ' + xhr.status + '; body: ' + xhr.responseText);
    }

    var deserializedResponse;
    try {
      deserializedResponse = JSONEnv.parse(xhr.responseText);
    } catch (err) {
      throw new AdaGaugeError('Error parsing response: ' + xhr.responseText);
    }

    if (!_.isObject(deserializedResponse)) {
      throw new AdaGaugeError('Response is not an object: ' + xhr.responseText);
    }

    if (!_.isObject(deserializedResponse.scores)) {
      throw new AdaGaugeError('Response lacks scores: ' + xhr.responseText);
    }

    return deserializedResponse;
  })
  .catch(function networkError() {
    throw new AdaGaugeError('Fetch failed');
  });
};

/**
 * @typedef {Object} FormattedInterestGroup
 * @property {Array.<string>} keywords
 *        The set of keywords defining the InterestGroup.
 * @property {string} tagGroup
 *        Some identifier for the TagGroup that is to supply the context used to
 *        compute the score for this Interestgroup. Must be consistent throughout
 *        the gauge request.
 */

/**
 * @typedef {Object} FormattedTagDescriptor
 * @property {string} viewId
 *        The ID of the View with which this tag is associated.
 *        Must be consistent with the ID used in AdaEvents in the context
 *        of a GaugeData.
 * @property {string} tag
 *        The API name of the tag.
 */

/**
 * @param {module:ControlTower~GaugeData} data
 * @returns {Object}
 *        The given data, massaged to conform to the gauge API's schema.
 * @private
 */
function formatRequestBody(data:import('ControlTower').GaugeData): any {
  var interests = _.reduce(data.interestGroups, function (acc, ig, id) {
    acc[id] = {
      keywords: ig.keywords,
      tagGroup: ig.tagGroupId,
    };

    return acc;
  }, {});

  var tagGroups = _.reduce(data.tagGroups, function (acc, tagGroup, id) {
    // Rename tagDescriptors' viewId -> eventName
    acc[id] = _.map(tagGroup.tags, function (tagDescriptor) {
      return {
        eventName: tagDescriptor.viewId,
        tag: tagDescriptor.tagApiName,
      };
    });

    return acc;
  }, {});

  return {
    interests: interests,
    tagGroups: tagGroups,
    posTags: PARTS_OF_SPEECH,
    visitor: {
      history: data.events,
    },
  };
}

